import { createFileRoute, Link } from "@tanstack/react-router";
import { EsPageShell, EsSection } from "@/components/site/EsPageShell";
import { esHead, esWebPageSchema, SITE } from "@/lib/es-pages";

const FAQS: { q: string; a: string }[] = [
  {
    q: "¿Medicare cubre Wegovy o Zepbound?",
    a: "La Parte D estándar sigue excluyendo los medicamentos usados para bajar de peso. Sin embargo, el programa de demostración Medicare GLP-1 Bridge, vigente desde el 1 de julio de 2026 hasta el 31 de diciembre de 2027, permite a los beneficiarios elegibles pagar un copago mensual de 50 dólares por ciertos GLP-1 recetados para bajar de peso. El Bridge es solo para quienes no tienen un diagnóstico elegible bajo la Parte D; si usted tiene diabetes, apnea del sueño, enfermedad cardiovascular o MASH, la solicitud se procesa por su plan regular de la Parte D.",
  },
  {
    q: "¿TRICARE exige probar antes otro medicamento para bajar de peso?",
    a: "Sí. TRICARE exige un ensayo de 3 meses con fentermina genérica, benzfetamina, dietilpropión o fendimetrazina que no haya logrado una pérdida del 5 por ciento del peso, a menos que usted tenga una contraindicación, haya tenido una reacción adversa, o haya perdido el 5 por ciento pero necesite reducir más. TRICARE también exige al menos 6 meses de modificación conductual y dietética documentada, y el prescriptor debe ser un proveedor de una instalación médica militar o de la red de TRICARE.",
  },
  {
    q: "¿Los planes de empleados federales (FEHB) cubren medicamentos para bajar de peso?",
    a: "Para el año de cobertura 2026, la Oficina de Administración de Personal (OPM) exigió que todos los planes FEHB cubran al menos un GLP-1 recetado para bajar de peso más al menos dos medicamentos orales adicionales contra la obesidad, así que existe algo de cobertura en todos los planes FEHB, aunque el medicamento preferido varía y se exige autorización previa. Para el año 2027, la OPM ha indicado a las aseguradoras que exigirá terapia conductual intensiva antes y durante la cobertura.",
  },
  {
    q: "¿Cómo empiezo a usar el Medicare GLP-1 Bridge?",
    a: "Primero se debe presentar y rechazar un reclamo de farmacia ante el procesador del Bridge, antes de que su proveedor envíe la autorización previa. Su proveedor presenta la solicitud; los pacientes no pueden hacerlo por su cuenta. Puede verificar su elegibilidad en Medicare.gov/glp1bridge o llamando al 1-800-MEDICARE.",
  },
  {
    q: "¿Qué pasa si tengo apnea del sueño y necesito Zepbound?",
    a: "TRICARE cubre Zepbound para la apnea obstructiva del sueño en adultos con un índice de apnea-hipopnea documentado de 15 o más eventos por hora y un IMC de 30 o más; Wegovy no está cubierto para esta indicación específica bajo TRICARE. Otros programas, como el Medicare GLP-1 Bridge, también reconocen la apnea del sueño como un diagnóstico que se procesa por una vía distinta.",
  },
];

export const Route = createFileRoute("/es/seguro/medicare-tricare-y-fehb")({
  head: () => {
    const base = esHead("insuranceMedicare");
    return {
      ...base,
      scripts: import.meta.env.SSR ? [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              esWebPageSchema("insuranceMedicare"),
              {
                "@type": "FAQPage",
                "@id": `${SITE}/es/seguro/medicare-tricare-y-fehb#faq`,
                inLanguage: "es-US",
                mainEntity: FAQS.map((f, i) => ({
                  "@type": "Question",
                  "@id": `${SITE}/es/seguro/medicare-tricare-y-fehb#faq-${i + 1}`,
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              },
            ],
          }),
        },
      ] : [],
    };
  },
  component: EsInsuranceMedicare,
});

function EsInsuranceMedicare() {
  return (
    <EsPageShell
      page="insuranceMedicare"
      title="Medicare, TRICARE y cobertura para empleados federales"
      intro="Los planes del gobierno siguen sus propias reglas, incluido el nuevo Medicare GLP-1 Bridge de 50 dólares y el requisito de terapia escalonada de TRICARE."
    >
      <EsSection id="empezar" heading="Antes de empezar">
        <p>
          Si aún no lo ha leído, el{" "}
          <Link to="/es/cobertura-de-seguro" className="font-medium text-brand-logo hover:underline">
            resumen general de cobertura de seguro
          </Link>{" "}
          explica los dos filtros y la vía alterna por diagnóstico en los que se apoyan mucho estos
          planes.
        </p>
      </EsSection>

      <EsSection id="medicare" heading="Medicare">
        <p>
          <strong>La regla por defecto:</strong> la Parte D de Medicare históricamente ha excluido
          los medicamentos usados para bajar de peso. Esa exclusión sigue rigiendo la cobertura
          estándar de la Parte D.
        </p>
        <p>
          <strong>Lo que cambió el 1 de julio de 2026:</strong> CMS lanzó el Medicare GLP-1 Bridge,
          un programa de demostración vigente del 1 de julio de 2026 al 31 de diciembre de 2027. Los
          beneficiarios elegibles de la Parte D pagan un <strong>copago mensual de 50 dólares</strong>{" "}
          por ciertos GLP-1 recetados para bajar de peso: todas las presentaciones de Wegovy
          (inyección y pastillas), Wegovy HD, Foundayo, y específicamente el KwikPen de Zepbound.
        </p>
        <p>
          <strong>Cómo funciona la elegibilidad.</strong> El Bridge es para la reducción de peso en
          adultos de 18 años o más que no tienen un diagnóstico elegible bajo la Parte D. Si usted
          tiene apnea obstructiva del sueño, MASH con fibrosis moderada a avanzada, diabetes tipo 2 o
          enfermedad cardiovascular establecida, su solicitud se procesa por su plan regular de la
          Parte D.
        </p>
        <p>Tres datos importantes sobre los 50 dólares:</p>
        <ul className="list-disc space-y-1 pl-6">
          <li>No cuentan para su deducible de la Parte D</li>
          <li>No cuentan para su límite anual de gastos de bolsillo</li>
          <li>La ayuda de subsidio por bajos ingresos no aplica a este copago</li>
        </ul>
        <p>
          <strong>Cómo empezar.</strong> Primero se debe presentar y rechazar un reclamo de farmacia
          ante el procesador del Bridge, antes de que su proveedor envíe la autorización previa. El
          proveedor presenta la solicitud; los pacientes no pueden hacerlo por su cuenta. Puede
          verificar su elegibilidad en Medicare.gov/glp1bridge o llamando al 1-800-MEDICARE.
        </p>
      </EsSection>

      <EsSection id="tricare" heading="TRICARE">
        <p>
          TRICARE es muy relevante en Virginia dada la comunidad militar de Hampton Roads y el norte
          de Virginia. Su criterio incluye un requisito de terapia escalonada que la mayoría de los
          planes comerciales no exige. Para adultos que buscan Wegovy o el Zepbound Pen Injector
          para el control de peso:
        </p>
        <ul className="list-disc space-y-1 pl-6">
          <li>
            IMC de 27 a 29 <strong>con</strong> al menos una condición relacionada con el peso, o IMC
            de 30 o más
          </li>
          <li>
            Certificación del proveedor de <strong>al menos 6 meses</strong> de modificación
            conductual y restricción dietética sin éxito, documentada en el expediente
          </li>
          <li>
            Un <strong>ensayo de 3 meses</strong> con fentermina genérica, benzfetamina,
            dietilpropión o fendimetrazina que no logró un 5 por ciento de pérdida de peso, a menos
            que exista una contraindicación, una reacción adversa, o ya se haya perdido el 5 por
            ciento pero se necesite reducir más
          </li>
          <li>
            No estar embarazada, no usar otro GLP-1, y no tener antecedentes personales ni
            familiares de cáncer medular de tiroides o síndrome MEN tipo 2
          </li>
        </ul>
        <p>
          <strong>El prescriptor debe ser un proveedor de una instalación médica militar o de la red
          de TRICARE</strong> que haya facturado a TRICARE por evaluarlo a usted y desarrollar un
          plan de tratamiento. Sin esto, la solicitud se rechaza automáticamente.
        </p>
        <p>
          <strong>De 12 a 17 años:</strong> solo está disponible la inyección de Wegovy, y se exige
          un IMC en el percentil 95 o superior. <strong>La aprobación dura 12 meses</strong>, con
          renovación anual que exige al menos un 5 por ciento de pérdida de peso desde el punto de
          partida (4 por ciento para los de 12 a 17 años). Para Zepbound y la apnea del sueño: adultos
          con un índice de apnea-hipopnea documentado de 15 o más eventos por hora y un IMC de 30 o
          más; Wegovy no está cubierto para esta indicación.
        </p>
      </EsSection>

      <EsSection id="fehb" heading="Empleados federales (FEHB)">
        <p>
          Esto es relevante en todo el norte de Virginia. Para el año de cobertura 2026, la OPM
          exigió que todos los planes FEHB cubran al menos un GLP-1 recetado para bajar de peso, más
          al menos dos medicamentos orales adicionales contra la obesidad, así que existe algo de
          cobertura en todos los planes FEHB, aunque el medicamento preferido varía. Se exige
          autorización previa, y la mayoría de las aseguradoras usan el estándar de IMC 30, o 27 con
          una condición asociada.
        </p>
        <p>
          <strong>Mirando hacia adelante:</strong> para el año de cobertura 2027, la OPM ha indicado a
          las aseguradoras que exigirá que los inscritos participen en terapia conductual intensiva
          antes y durante la cobertura de medicamentos contra la obesidad. Se espera que los
          requisitos de FEHB se vuelvan más estrictos.
        </p>
      </EsSection>

      <EsSection id="preguntas" heading="Preguntas frecuentes">
        <dl className="space-y-4">
          {FAQS.map((f, i) => (
            <div key={f.q} id={`faq-${i + 1}`}>
              <dt className="font-medium text-brand-deep">{f.q}</dt>
              <dd className="mt-1">{f.a}</dd>
            </div>
          ))}
        </dl>
      </EsSection>

      <EsSection id="mas" heading="Más guías de cobertura">
        <p>
          Consulte también la cobertura de{" "}
          <Link
            to="/es/seguro/plan-comercial-y-marketplace"
            className="font-medium text-brand-logo hover:underline"
          >
            planes comerciales y del Marketplace
          </Link>
          , de{" "}
          <Link
            to="/es/seguro/medicaid-y-cardinal-care"
            className="font-medium text-brand-logo hover:underline"
          >
            Medicaid y Cardinal Care
          </Link>
          , o vuelva al{" "}
          <Link to="/es/cobertura-de-seguro" className="font-medium text-brand-logo hover:underline">
            resumen general de cobertura
          </Link>
          . Si tiene preguntas específicas sobre su caso, {" "}
          <Link to="/es/contacto" className="font-medium text-brand-logo hover:underline">
            contáctenos
          </Link>
          .
        </p>
      </EsSection>
    </EsPageShell>
  );
}
